'use client';
import { memo, lazy, Suspense } from 'react';
import Navbar from '@/components/layout/Navbar';
import Hero from '@/components/home/Hero';
import ManuallyVerifiedBanner from '@/components/trust/ManuallyVerifiedBanner';
import { ReferPharmacyCTA } from '@/components/referral/ReferPharmacyCTA';
import DeferredSection from '@/components/home/DeferredSection';
import { Skeleton } from '@/components/ui/skeleton';

// Defer heavy SEO schemas - not needed for initial paint

// Lazy load conversion components - they aren't needed for initial render


// Lazy load LocalProvider - triggers edge function call, defer until local sections are visible

// Lazy load below-the-fold components with prefetch hints
const HowItWorks = lazy(() => import('@/components/home/HowItWorks'));
const Features = lazy(() => import('@/components/home/Features'));
const PharmacyPromo = lazy(() => import('@/components/home/PharmacyPromo'));
const CTASection = lazy(() => import('@/components/home/CTASection'));
const PricingFAQ = lazy(() => import('@/components/home/PricingFAQ'));
const PatientProtection = lazy(() => import('@/components/home/PatientProtection'));
const HomeSEOLinks = lazy(() => import('@/components/home/HomeSEOLinks'));
const Footer = lazy(() => import('@/components/layout/Footer'));

// Defer accessibility - important but not render-critical
// 2026-06-02 Cortana (UAT msg from Julian): SkipLinks removed at his request.
// He didn't want the 'Skip to main content' surface even when hidden behind
// focus-visible. Trade-off: -3 to -5 Lighthouse a11y points vs zero risk of
// accidental URL hash pollution / visible cyan button. Re-add only if a
// formal a11y audit demands it. ScreenReaderAnnouncer remains - it's the
// other piece of the accessibility bundle and renders nothing visually.
const ScreenReaderAnnouncer = lazy(() => import('@/components/accessibility').then(m => ({ default: m.ScreenReaderAnnouncer })));

// Minimal loading placeholders - different sizes for different sections
const SectionSkeleton = memo(() => (
  <div className="py-12 px-4">
    <div className="max-w-4xl mx-auto space-y-4">
      <Skeleton className="h-6 w-1/4 mx-auto" />
      <Skeleton className="h-4 w-1/2 mx-auto" />
    </div>
  </div>
));
SectionSkeleton.displayName = 'SectionSkeleton';

const CardsSkeleton = memo(() => (
  <div className="py-12 px-4">
    <div className="max-w-6xl mx-auto">
      <Skeleton className="h-6 w-1/4 mx-auto mb-8" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Skeleton className="h-48" />
        <Skeleton className="h-48" />
        <Skeleton className="h-48" />
      </div>
    </div>
  </div>
));
CardsSkeleton.displayName = 'CardsSkeleton';

const Index = memo(() => {
  return (
    <>
      {/* Accessibility Components - SkipLinks removed per Julian's request 2026-06-02 */}
      <Suspense fallback={null}>
        <ScreenReaderAnnouncer />
      </Suspense>

      {/*
        2026-09 spam-update recovery. Removed from the homepage: the rotating
        the fake "someone just saved" toast, the fabricated total-saved
        counter, stock-photo testimonials, invented case studies, the
        pharmacy-count and compliance badge rows, the fake demo bid
        cards, the invented average-savings line and three duplicate
        Organization schemas. Every section that remains describes what the
        product does; none states a result that has not happened yet.
      */}

      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main id="main-content" className="flex-grow">
          <Hero />

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-center">
            <ManuallyVerifiedBanner context="all" variant="inline" />
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4" style={{ minHeight: 48 }}>
            <ReferPharmacyCTA variant="banner" context="homepage" />
          </div>

          <DeferredSection fallback={<SectionSkeleton />} eager>
            <HowItWorks />
          </DeferredSection>

          <DeferredSection fallback={<SectionSkeleton />} rootMargin="400px">
            <PatientProtection />
          </DeferredSection>

          <DeferredSection fallback={<SectionSkeleton />} rootMargin="400px">
            <PricingFAQ />
          </DeferredSection>

          <DeferredSection fallback={<CardsSkeleton />} rootMargin="400px">
            <Features />
          </DeferredSection>

          <DeferredSection fallback={null} rootMargin="300px">
            <PharmacyPromo />
          </DeferredSection>

          <DeferredSection fallback={null} rootMargin="300px">
            <HomeSEOLinks />
          </DeferredSection>

          <DeferredSection fallback={null} rootMargin="300px">
            <CTASection />
          </DeferredSection>
        </main>
        <DeferredSection rootMargin="200px">
          <footer id="footer">
            <Footer />
          </footer>
        </DeferredSection>
      </div>
    </>
  );
});

Index.displayName = 'Index';

export default Index;
